import "./app-store-release.css";
import { PRODUCT, WEB_ROUTES } from "./config";

type OutcomeStatus = "active" | "proof" | "external" | "not-ready";

interface GitHubLink {
  label: string;
  href: string;
}

interface TaskRef {
  name: string;
  id: string;
}

interface OutcomeRow {
  title: string;
  status: OutcomeStatus;
  statusLabel: string;
  current: string;
  github: GitHubLink[];
  estimate: string;
  tasks: TaskRef[];
  nextProof: string;
}

interface OutcomeGroup {
  id: string;
  title: string;
  summary: string;
  rows: OutcomeRow[];
}

const GITHUB = "https://github.com/markitics/agenda";
const APPLE_GUIDELINES = "https://developer.apple.com/app-store/review/guidelines/";

function issue(number: number): string {
  return `${GITHUB}/issues/${number}`;
}

function pull(number: number): string {
  return `${GITHUB}/pull/${number}`;
}

const tasks = {
  appStore: {
    name: "🍏 AppStore",
    id: "019fc8ce-ce29-7450-8c50-25170a532d91",
  },
  master: {
    name: "💃 MasterAgent",
    id: "019fb554-a5b2-7783-a12d-c529e12fa5e9",
  },
  search: {
    name: "🔍 SearchAgent",
    id: "019fb457-455c-7e22-ab3f-014fc39d278d",
  },
  stripeBackend: {
    name: "💳 StripeMobile491Agent",
    id: "019fcfc8-f57f-7fb0-9ac2-1b8e61d07d21",
  },
  iosPaidAccess: {
    name: "🧪 NativeOwnerCases492Agent",
    id: "/root/native_owner_cases492",
  },
  stripeQa: {
    name: "🧪 MasterAgent · #493",
    id: "019fb554-a5b2-7783-a12d-c529e12fa5e9",
  },
  stripeLiveConfig: {
    name: "💰 StripeLiveConfig504Agent",
    id: "019fcb8a-8b26-70d2-99ec-5c3cc5e4ca0e",
  },
  reviewerAccess: {
    name: "🔑 MasterAgent · #446",
    id: "019fb554-a5b2-7783-a12d-c529e12fa5e9",
  },
  signupAcceptance: {
    name: "✉️ SignupAcceptance323Agent",
    id: "019fcb32-d4cd-7c43-a30d-ffc138cda5b1",
  },
  deletion: {
    name: "🗑️ DeletionRelease496Agent",
    id: "/root/deletion_release496",
  },
  sync: {
    name: "🔄 Sync390Agent",
    id: "/root/sync390_bug",
  },
} satisfies Record<string, TaskRef>;

const outcomeGroups: OutcomeGroup[] = [
  {
    id: "bugs",
    title: "Bug outcomes",
    summary: "Four grouped outcomes, not four individual bugs or GitHub issues.",
    rows: [
      {
        title: "Private data absent from the exact public candidate",
        status: "proof",
        statusLabel: "Proof open",
        current:
          "Credential fences, private-catalog removal, archive scanning, and required-reason privacy-manifest checks are merged. Build 36 is internal-only; no public candidate/archive exists yet, so candidate-bound clean-install, account-switch, and private-content proof remain open.",
        github: [
          { label: "#417", href: issue(417) },
          { label: "#444", href: issue(444) },
          { label: "#458", href: issue(458) },
          { label: "#315", href: issue(315) },
        ],
        estimate: "M · 4–12h after a public candidate is frozen",
        tasks: [tasks.master, tasks.appStore],
        nextProof:
          "Candidate SHA, signed archive/IPA scan, empty clean account, account-switch denial, and physical acceptance all agree.",
      },
      {
        title: "Reveal and swipe complete exactly once on a real iPhone",
        status: "proof",
        statusLabel: "Device proof queued",
        current:
          "Build 36 is locked as immutable internal-only evidence and remains installed on the dedicated Mark 13 Pro. Fresh read-only device state at 22:40 EDT found the phone wired, paired, unlocked, and available, but no app, TestFlight, mirroring, feature, account, or personal iPhone 17 action ran because the Build 37 source and package gates remain open. The owner-only queue still has one existing device request; no duplicate was created.",
        github: [
          { label: "#400", href: issue(400) },
          { label: "#315", href: issue(315) },
          { label: "current device readback", href: `${issue(315)}#issuecomment-5186917465` },
        ],
        estimate: "S · ≤4h to run proof; repair estimate unknown if it fails",
        tasks: [tasks.master],
        nextProof:
          "Named build and iPhone complete exactly one Reveal plus the recorded swipe/Undo matrix without a state reset.",
      },
      {
        title: "Signed-out, offline, and reauthenticated play stays truthful",
        status: "active",
        statusLabel: "Active",
        current:
          "Start, parked-card identity, offline state, and reauthentication must agree. The generic warning screenshot is now attached to #327; personal Deck imagery was excluded.",
        github: [
          { label: "#327", href: issue(327) },
          {
            label: "safe screenshot",
            href: `${issue(327)}#issuecomment-5175298490`,
          },
        ],
        estimate: "M · focused-work estimate unknown until the failing paths are rerun",
        tasks: [tasks.master],
        nextProof:
          "Deterministic revocation/offline/replay tests and direct candidate acceptance preserve the exact parked card and recoverable state.",
      },
      {
        title: "Saved-share recovery, outbound edits, and Sync status agree",
        status: "active",
        statusLabel: "Foreground Save defect open",
        current:
          "Saved-share and status fixes exist, but #390 proves foreground Save can remain Waiting to sync because it schedules no upload. #197 owns that correction. The safe #371 screenshot is public; the raw #390 capture remains private.",
        github: [
          { label: "#371", href: issue(371) },
          {
            label: "safe screenshot",
            href: `${issue(371)}#issuecomment-5175305293`,
          },
          { label: "#390", href: issue(390) },
          { label: "#197", href: issue(197) },
        ],
        estimate: "M · focused-work estimate unknown until the #197 correction is accepted",
        tasks: [tasks.sync],
        nextProof:
          "After durable local Save, #197 dispatches exactly one bounded outbound attempt; ambiguous/offline states stay recoverable; then affected-device retry and Sync counts agree.",
      },
    ],
  },
  {
    id: "product",
    title: "Product and account readiness",
    summary: "Outcomes a new customer or App Review must complete without Mark improvising.",
    rows: [
      {
        title: "A new person signs up and saves a first card",
        status: "proof",
        statusLabel: "Acceptance open",
        current:
          "The first-party email path exists and the controlled project mailbox is confirmed unregistered with zero writes. One real controlled signup acceptance is still awaiting its existing authorization item; no duplicate request was created.",
        github: [
          { label: "#323", href: issue(323) },
          { label: "#444", href: issue(444) },
          { label: "#12", href: issue(12) },
        ],
        estimate: "M · 4–12h of focused acceptance work",
        tasks: [tasks.signupAcceptance, tasks.master],
        nextProof:
          "A new address yields one principal, workspace, Me audience, scanner-safe session, empty state, and one findable saved card.",
      },
      {
        title: "Account deletion is fulfilled, not merely requested",
        status: "active",
        statusLabel: "Synthetic rehearsal merged",
        current:
          "In-app initiation, session revocation, blocked sign-in, pending state, release-operation schema, and reviewed Worker source are deployed. PR #563 adds an accepted source-only synthetic integration rehearsal, but production tables remain empty and the deletion promise is deliberately inactive. No real deletion, completion email or provider delivery, dedicated sender, accepted candidate, or activation proof exists. #496 remains open; stricter #445 is outside the first-submission milestone.",
        github: [
          { label: "#496", href: issue(496) },
          { label: "#445", href: issue(445) },
          { label: "PR #461", href: pull(461) },
          { label: "PR #520", href: pull(520) },
          { label: "PR #563", href: pull(563) },
        ],
        estimate: "M · 4–12h for #496; stricter #445 completion estimate unknown",
        tasks: [tasks.deletion, tasks.master],
        nextProof:
          "Bind PR #520's false-gated adapter and PR #563's synthetic rehearsal to the exact candidate, operator flow, authorized sender, and real provider delivery; then prove request, all-session revocation, pending state, reviewed timeframe/local-data truth, and completion delivery. Do not call #445 cleared.",
      },
      {
        title: "Stripe paid access works through the full lifecycle",
        status: "active",
        statusLabel: "Source and live-state reconciliation open",
        current:
          "Billed terms are $5/month or $19/year—about $1.15/week and $0.36/week; $1.14/week and weekly billing are unsupported. The U.S.-only Stripe architecture is policy-acceptable in principle under Apple Guideline 3.1.1(a), without Apple IAP or the external-purchase entitlement; this is not submission acceptance. Fresh bounded GET reconciliation proves launch Account 2 is healthy U.S./USD and exactly empty for Products, Prices, Customer Portal configurations, and webhook endpoints. Its current-main four-action setup plan is independently accepted; the one existing For Mark action is Chrome Touch ID/security-key verification to create the restricted key. No key, catalog, Portal, webhook, or runtime authority exists yet. #491 accepted source/deploy omit mobile Checkout `origin_context=mobile_app`. PR #572's accepted four-path reviewer source is merged at main e580242 and released the shared Worker route, but it does not close the mobile-origin gap. Draft PR #574 remains HOLD: bind trial eligibility to the post-provider request time and use `Stripe-Version: 2025-07-30.basil` only for Checkout Session creation carrying `origin_context`, while preserving `2022-08-01` elsewhere. Accepted PR #578 merged the #576 race corrections; accepted PR #569 merged synthetic owner-case evidence. PR #579 merged source-reviewed paid-access listing/review notes, but Apple still has the prior description and no saved final notes. Submission remains HOLD for PR #574 acceptance/deploy, Account-2 setup/readback, safe Apple metadata write/readback, deployed reviewer access, and public-candidate proof.",
        github: [
          { label: "#45", href: issue(45) },
          { label: "#467", href: issue(467) },
          { label: "#491", href: issue(491) },
          { label: "#492", href: issue(492) },
          { label: "#493", href: issue(493) },
          { label: "#504", href: issue(504) },
          { label: "#576", href: issue(576) },
          { label: "Apple 3.1.1(a)", href: `${APPLE_GUIDELINES}#business` },
          { label: "PR #541", href: pull(541) },
          { label: "PR #544", href: pull(544) },
          { label: "PR #546", href: pull(546) },
          { label: "PR #569", href: pull(569) },
          { label: "PR #570", href: pull(570) },
          { label: "PR #572", href: pull(572) },
          { label: "PR #574", href: pull(574) },
          { label: "PR #578", href: pull(578) },
          { label: "PR #579", href: pull(579) },
          { label: "PR #570 merged state", href: `${issue(467)}#issuecomment-5186950372` },
          { label: "post-merge Portal HOLD", href: `${issue(467)}#issuecomment-5186952687` },
          { label: "mobile origin gap", href: `${issue(491)}#issuecomment-5186515162` },
          { label: "PR #574 current HOLD", href: `${pull(574)}#issuecomment-5187731861` },
          { label: "Account 2 live-empty plan", href: `${issue(504)}#issuecomment-5187745971` },
          { label: "single Touch ID action", href: `${issue(504)}#issuecomment-5187755793` },
        ],
        estimate: "L · >12h of focused work across the four focused lanes",
        tasks: [
          tasks.appStore,
          tasks.stripeBackend,
          tasks.iosPaidAccess,
          tasks.stripeQa,
          tasks.stripeLiveConfig,
        ],
        nextProof:
          "Correct, rerun, independently accept, and merge PR #574's post-provider request-time binding and Checkout-only `2025-07-30.basil` route. Then separately deploy/read back the combined Worker; after the existing Touch ID action, apply/read back only Account 2's accepted four-action plan before lifecycle and native/device proof.",
      },
    ],
  },
  {
    id: "submission",
    title: "Submission and approval",
    summary: "Store package, final candidate, App Review, and public availability remain distinct states.",
    rows: [
      {
        title: "Convoful naming and upgrade continuity match",
        status: "proof",
        statusLabel: "Source merged; candidate proof open",
        current:
          "Apple's listing and submission package say Convoful. Independently accepted PR #571 merged the exact 17-path visible-only Convoful source as main b2068fa after normally signed units 732/732, the full UI matrix 16/16, focused geometry/privacy 1/1, and candidate-verifier checks passed. This is source-only proof: Build 36 remains the immutable Agenda! internal-only build, and no Build 37-or-later public candidate, archive, clean-install proof, or dedicated-iPhone preserved-data upgrade proof exists. Bundle IDs, App Group, Keychain, API host, data, TestFlight lineage, and historical Agenda! release evidence remain unchanged.",
        github: [
          { label: "#32", href: issue(32) },
          { label: "#315", href: issue(315) },
          { label: "#426", href: issue(426) },
          { label: "#429", href: issue(429) },
          { label: "PR #558", href: pull(558) },
          { label: "PR #571", href: pull(571) },
          { label: "current naming handoff", href: `${issue(32)}#issuecomment-5185493968` },
        ],
        estimate: "M · 4–12h after a Build 37-or-later public candidate is frozen",
        tasks: [tasks.master, tasks.appStore],
        nextProof:
          "Freeze a Build 37-or-later public candidate, then prove its upgrade from installed Build 36 and a clean install show Convoful across app, Share extension, cards, icon, and About while every compatibility identifier and private record remains intact.",
      },
      {
        title: "Metadata and privacy-safe screenshots are saved in App Store Connect",
        status: "active",
        statusLabel: "Metadata partial; screenshots absent",
        current:
          "Apple still has the prior 855-character description, plus Lifestyle primary category with no secondary category, the standard Apple EULA, manual release, published App Privacy, Free $0.00 pricing, and USA-only availability. Accepted PR #579's target description is 1,009 characters and includes the Stripe disclosure, but it is not saved or read back. The guarded metadata planner rejects any non-null prior description, so #429 needs a focused allowlist correction before a safe write/readback. The prepared App Review notes are 2,046 UTF-8 bytes—below Apple's 4,000-byte limit—but still await final reviewer and deletion data. Accepted PR #575 improved the Build 37+ package verifier, but #429 remains open: `submission_ready=false`, candidate, build, source, and evidence are null, fixture is false, 11 blockers remain, the selected build is null, and eligible assets and Apple screenshots are zero. Five local previews remain synthetic, legacy-branded, noncandidate, and ineligible.",
        github: [
          { label: "#429", href: issue(429) },
          { label: "#431", href: issue(431) },
          { label: "18:03 Apple readback", href: `${issue(429)}#issuecomment-5185148214` },
          { label: "PR #523", href: pull(523) },
          { label: "PR #551", href: pull(551) },
          { label: "PR #553", href: pull(553) },
          { label: "PR #575", href: pull(575) },
          { label: "PR #579", href: pull(579) },
        ],
        estimate: "M · 4–12h of focused asset and entry work",
        tasks: [tasks.appStore],
        nextProof:
          "Correct and review #429's narrow prior-description allowlist, then guardedly save and read back the 1,009-character description. Finalize the 2,046-byte notes with reviewer/deletion data; candidate-bound build selection and privacy-safe screenshots remain later gates.",
      },
      {
        title: "The app-specific DSA declaration is decided and saved",
        status: "external",
        statusLabel: "Mark decision required",
        current:
          "Accepted PR #557 prepares NOT_TRADER as the high-confidence app-specific candidate for the exact United States-only launch. Mark has not made the DSA declaration decision, and no DSA value has been saved or read back from Apple.",
        github: [
          { label: "#426", href: issue(426) },
          { label: "#429", href: issue(429) },
          { label: "PR #557", href: pull(557) },
        ],
        estimate: "S · ≤4h after Mark's decision",
        tasks: [tasks.appStore],
        nextProof:
          "After Mark decides, save the app-specific declaration in App Store Connect and read back the exact value without changing any account-level DSA status.",
      },
      {
        title: "Content-rights declaration matches the accepted candidate",
        status: "proof",
        statusLabel: "Apple answer absent",
        current:
          "Apple currently has no content-rights answer. The prepared candidate says the app uses third-party content because customers can save links, source text, and source media; it must not be attested until the exact public candidate and #444 content set are accepted.",
        github: [
          { label: "#429", href: issue(429) },
          { label: "#444", href: issue(444) },
          { label: "PR #557", href: pull(557) },
        ],
        estimate: "S · ≤4h after the public candidate is accepted",
        tasks: [tasks.appStore, tasks.master],
        nextProof:
          "Reconcile the exact public starter and customer-selected content, save Uses Third-Party Content, and read back Apple's value.",
      },
      {
        title: "Age-rating questionnaire matches the accepted candidate",
        status: "proof",
        statusLabel: "Questionnaire unanswered",
        current:
          "Apple's age-rating questionnaire remains entirely unanswered. A 4+ candidate is prepared, but every answer must be checked against the exact post-#444 public starter content before it is saved.",
        github: [
          { label: "#429", href: issue(429) },
          { label: "#444", href: issue(444) },
          { label: "PR #557", href: pull(557) },
        ],
        estimate: "S · ≤4h after the public candidate is accepted",
        tasks: [tasks.appStore, tasks.master],
        nextProof:
          "Answer the complete questionnaire from the exact candidate, save it, and read back Apple's resulting age rating and every response.",
      },
      {
        title: "Export-compliance answer is saved and read back",
        status: "proof",
        statusLabel: "Candidate answer prepared",
        current:
          "The prepared answer is Uses Encryption: Yes and Uses Non-Exempt Encryption: No. The app relies on Apple's HTTPS, CryptoKit, and Keychain APIs and declares ITSAppUsesNonExemptEncryption=false, but the App Store Connect answer has not been saved or read back.",
        github: [
          { label: "#426", href: issue(426) },
          { label: "#429", href: issue(429) },
          { label: "PR #557", href: pull(557) },
        ],
        estimate: "S · ≤4h of focused entry and readback",
        tasks: [tasks.appStore],
        nextProof:
          "Recheck the exact archive, save the exempt-encryption answers, and read back Apple confirmation with no documentation request.",
      },
      {
        title: "App Review contact is complete and callable",
        status: "external",
        statusLabel: "Mark confirmation required",
        current:
          "The prepared contact is Mark Moriarty at the monitored AppStore@markmoriarty.com address. A GET-only scan found a same-team App Review phone saved on another app, masked ending 92, with two stable readbacks; whether it is currently callable, controlled by Mark or an authorized reviewer contact, and approved for Convoful reuse is unproven. Mark may instead provide another authorized callable number through the existing single For Mark item or email. Convoful's ordinary App Review Detail remains absent.",
        github: [
          { label: "#426", href: issue(426) },
          { label: "#429", href: issue(429) },
          { label: "PR #557", href: pull(557) },
        ],
        estimate: "S · ≤4h after Mark confirms ending 92 may be reused or provides another authorized callable number",
        tasks: [tasks.appStore],
        nextProof:
          "Either confirm the saved App Review number ending 92 is currently callable, reachable by Mark or an authorized reviewer contact, and reusable for Convoful, or provide another authorized callable number through the existing single request; then save all four Convoful contact fields and read them back exactly.",
      },
      {
        title: "Privacy answers and durable reviewer access match reality",
        status: "active",
        statusLabel: "Privacy saved; reviewer grant absent",
        current:
          "Apple App Privacy is published with 12 reviewed data types, all App Functionality / Linked / Not Tracking; Payment Info is unselected because checkout stays Stripe-hosted. PR #572's independently accepted exact four-path source is merged as main e580242. Its migration has not been applied, the Worker has not been deployed, and production still has zero reviewer grants; no reviewer principal, credential, session, or grant exists. The parked For Mark authorization remains the single request and does not turn merged source into runtime or credential proof.",
        github: [
          { label: "#429", href: issue(429) },
          { label: "#431", href: issue(431) },
          { label: "#444", href: issue(444) },
          { label: "#496", href: issue(496) },
          { label: "#445", href: issue(445) },
          { label: "#446", href: issue(446) },
          { label: "PR #533", href: pull(533) },
          { label: "PR #545", href: pull(545) },
          { label: "PR #550", href: pull(550) },
          { label: "PR #572", href: pull(572) },
          { label: "PR #572 merge proof", href: `${pull(572)}#issuecomment-5187609541` },
        ],
        estimate: "M · focused-work estimate unknown until #496 and #446 converge",
        tasks: [tasks.reviewerAccess, tasks.appStore],
        nextProof:
          "After PR #574's shared-route integration is accepted, separately apply PR #572's migration and deploy/read back the exact combined Worker. Only then may the existing authorization permit one least-privilege synthetic reviewer credential and its grant/revoke, clean-install, and App Review readback proof.",
      },
      {
        title: "United States-only availability is accepted and read back",
        status: "proof",
        statusLabel: "Saved and read back",
        current:
          "Fresh GET-only Apple readback proves exactly United States available on app release, all other 174 territories unavailable, automatic new-territory availability disabled, and no preorder/date. This is saved launch configuration—not submission, approval, or public availability.",
        github: [
          { label: "#426", href: issue(426) },
          { label: "#429", href: issue(429) },
        ],
        estimate: "S · no focused configuration work remains; public proof waits on release",
        tasks: [tasks.appStore],
        nextProof:
          "After approval and release, public App Store availability and an install from the U.S. storefront agree with the saved configuration.",
      },
      {
        title: "Final candidate is accepted, submitted, approved, and released",
        status: "not-ready",
        statusLabel: "No public candidate",
        current:
          "Apple has builds 1–36 only. Build 36 is VALID, unexpired, tester-visible, installed on the dedicated iPhone 13, and immutable Agenda! internal-only evidence—not App Review eligible. Current source says Convoful after merged PR #571. PR #572 merged accepted reviewer-access source, but its migration, Worker deploy, credential, and runtime proof remain absent. Draft PR #574 is HOLD for post-provider request time and Checkout-only Stripe API-version corrections. Account 2 is verified live-empty with an accepted four-action plan and one open Touch ID key-creation action; no catalog, Portal, webhook, or runtime proof exists. PR #575 merged core fail-closed candidate tooling, and PR #577 merged its upload-fixture correction. No Build 37, archive, upload, selected App Store build, candidate, screenshots, or submission exists; candidate identity is null. Deletion rehearsal, signed-archive scan, and device acceptance remain open. These facts do not support an honest six-hour submission claim.",
        github: [
          { label: "#12", href: issue(12) },
          { label: "#315", href: issue(315) },
          { label: "#426", href: issue(426) },
          { label: "#429", href: issue(429) },
          { label: "#446", href: issue(446) },
          { label: "#491", href: issue(491) },
          { label: "#492", href: issue(492) },
          { label: "#547", href: issue(547) },
          { label: "current device/build readback", href: `${issue(315)}#issuecomment-5186917465` },
          { label: "#576", href: issue(576) },
          { label: "PR #546", href: pull(546) },
          { label: "PR #548", href: pull(548) },
          { label: "PR #569", href: pull(569) },
          { label: "PR #570", href: pull(570) },
          { label: "PR #571", href: pull(571) },
          { label: "PR #572", href: pull(572) },
          { label: "PR #574", href: pull(574) },
          { label: "PR #575", href: pull(575) },
          { label: "PR #577", href: pull(577) },
          { label: "PR #578", href: pull(578) },
          { label: "PR #579", href: pull(579) },
        ],
        estimate: "L · focused-work estimate unknown until upstream outcomes close",
        tasks: [tasks.appStore, tasks.master],
        nextProof:
          "Accept, merge, deploy, and read back PR #574; apply/read back Account 2's accepted plan after the existing Touch ID action; deploy and rehearse reviewer access; complete deletion/lifecycle proof; then freeze one public candidate, pass archive/device/screenshots, select its build, and wait for Mark's final `confirm`.",
      },
    ],
  },
];

function setMetaDescription(content: string): void {
  let meta = document.querySelector<HTMLMetaElement>('meta[name="description"]');
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "description";
    document.head.append(meta);
  }
  meta.content = content;
}

function setCanonical(href: string): void {
  let canonical = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!canonical) {
    canonical = document.createElement("link");
    canonical.rel = "canonical";
    document.head.append(canonical);
  }
  canonical.href = href;
}

function githubLinkMarkup(link: GitHubLink): string {
  return `<a href="${link.href}" target="_blank" rel="noreferrer">${link.label}<span aria-hidden="true">↗</span></a>`;
}

function taskMarkup(task: TaskRef): string {
  return `<span class="asr-task"><strong>${task.name}</strong><code>${task.id}</code></span>`;
}

function outcomeRowMarkup(row: OutcomeRow, groupId: string): string {
  return `
    <tr class="asr-outcome-row" data-status="${row.status}" data-group="${groupId}">
      <td class="asr-status-cell" data-label="Status"><span>${row.statusLabel}</span></td>
      <th class="asr-gate-cell" scope="row" data-label="Gate">
        <h3>${row.title}</h3>
        <p>${row.current}</p>
      </th>
      <td class="asr-github-cell" data-label="GitHub issues">
        <div>${row.github.map(githubLinkMarkup).join("")}</div>
      </td>
      <td class="asr-estimate-cell" data-label="Size / focused work">${row.estimate}</td>
      <td class="asr-tasks-cell" data-label="Owner / Codex task">
        <div class="asr-task-list">${row.tasks.map(taskMarkup).join("")}</div>
      </td>
      <td class="asr-proof-cell" data-label="Next proof">${row.nextProof}</td>
    </tr>`;
}

function outcomeGroupMarkup(group: OutcomeGroup): string {
  return `
    <tbody id="${group.id}">
      <tr class="asr-group-row">
        <th colspan="6" scope="rowgroup">
          <strong>${group.title}</strong>
          <span>${group.rows.length} open outcome rows · ${group.summary}</span>
        </th>
      </tr>
      ${group.rows.map((row) => outcomeRowMarkup(row, group.id)).join("")}
    </tbody>`;
}

export function renderAppStoreRelease(root: HTMLElement): void {
  document.documentElement.dataset.surface = "app-store-release";
  document.title = `App Store release · ${PRODUCT.name}`;
  setMetaDescription(
    "A precise, evidence-linked operational dashboard for Convoful's first public App Store submission and release.",
  );
  setCanonical(`${WEB_ROUTES.canonicalOrigin}${WEB_ROUTES.appStoreRelease}`);
  const skipLink = document.querySelector<HTMLAnchorElement>(".skip-link");
  if (skipLink) skipLink.href = "#app-store-main";

  const openOutcomeCount = outcomeGroups.reduce((count, group) => count + group.rows.length, 0);
  const bugOutcomeCount = outcomeGroups.find(({ id }) => id === "bugs")?.rows.length ?? 0;

  root.innerHTML = `
    <div class="asr-shell">
      <header class="asr-site-header">
        <a class="asr-brand" href="${WEB_ROUTES.marketing}" aria-label="${PRODUCT.name} home">
          <span class="brand-mark brand-mark-small" aria-hidden="true">${PRODUCT.brandMark}</span>
          <span>${PRODUCT.name}</span>
        </a>
        <nav class="asr-nav" aria-label="Release dashboard">
          <a href="${WEB_ROUTES.markAttention}">For Mark</a>
          <a href="${issue(426)}" target="_blank" rel="noreferrer">Tracker source #426</a>
        </nav>
      </header>

      <main id="app-store-main" tabindex="-1">
        <section class="asr-intro" aria-labelledby="asr-title">
          <div>
            <p class="eyebrow">Operational dashboard · evidence snapshot 5 Aug 2026 · 01:05 EDT</p>
            <h1 id="asr-title">App Store release operations</h1>
            <p class="asr-intro-copy">
              Open work, exact owners, focused-work estimates, and the proof required to close each release outcome.
            </p>
          </div>
          <aside class="asr-assessment" aria-labelledby="assessment-title">
            <p class="eyebrow" id="assessment-title">Submission-day assessment</p>
            <strong>Submission is on HOLD. No evidence-backed submission day yet.</strong>
            <p>
              The pre-PR #575 release QA in <a href="${pull(568)}" target="_blank" rel="noreferrer">PR #568</a>
              reports 122 missing proof cells, four missing Apple review readbacks, five checklist gates, and five owner integrations.
              <a href="${pull(575)}" target="_blank" rel="noreferrer">PR #575</a> then merged a new fail-closed package digest;
              #429 remains open and the package is <code>submission_ready=false</code> with 11 blockers, candidate, build, source,
              and evidence null, and zero eligible assets or Apple screenshots. The 122/4/5/5 snapshot is stale for this new
              package, and QA reconciliation to it remains open.
              The verdict remains HOLD.
              There is still no public candidate or submission.
              No evidence supports an honest six-hour submission claim. The immediate external step is the existing
              Stripe Account-2 Touch ID action; work continues elsewhere.
            </p>
          </aside>
        </section>

        <section class="asr-milestone" aria-labelledby="milestone-title">
          <div>
            <p class="eyebrow">GitHub scope</p>
            <h2 id="milestone-title">
              <a href="${GITHUB}/milestone/17" target="_blank" rel="noreferrer">Milestone #17</a>:
              11 open · 8 closed · 19 total issues
            </h2>
          </div>
          <p>
            <strong>Live GitHub milestone API readback at 4 Aug 2026 · 23:19 EDT reports 11 open and 8 closed.</strong>
            Milestone #17 is the narrower submission/approval bucket.
            It is not one-to-one with this broader release-readiness tracker. One outcome row can link several issues,
            and prerequisite or physical-proof issues can sit outside the milestone.
          </p>
        </section>

        <section class="asr-summary" aria-label="Tracker count definitions">
          <div>
            <strong>${openOutcomeCount}</strong>
            <span>open outcome rows—not ${openOutcomeCount} GitHub issues</span>
          </div>
          <div>
            <strong>${bugOutcomeCount}</strong>
            <span>grouped bug outcomes—not a count of individual issues</span>
          </div>
          <div>
            <strong>0</strong>
            <span>public App Review submissions</span>
          </div>
          <div>
            <strong>HOLD</strong>
            <span>current pre-submission verdict</span>
          </div>
        </section>

        <section class="asr-legend" aria-label="Estimate legend">
          <strong>Estimate legend</strong>
          <span><b>S</b> ≤4h</span>
          <span><b>M</b> 4–12h</span>
          <span><b>L</b> &gt;12h</span>
          <p>Focused-work estimates exclude Apple, device, and user waiting. They are not promises; “unknown” means evidence does not support a responsible estimate.</p>
        </section>

        <section class="asr-table-section" aria-labelledby="outcomes-title">
          <div class="asr-table-heading">
            <div>
              <p class="eyebrow">Broader release-readiness tracker</p>
              <h2 id="outcomes-title">Open outcomes and next proof</h2>
            </div>
            <a href="${issue(426)}" target="_blank" rel="noreferrer">GitHub tracker #426 <span aria-hidden="true">↗</span></a>
          </div>
          <div class="asr-table-wrap">
            <table>
              <thead>
                <tr>
                  <th scope="col">Status</th>
                  <th scope="col">Gate</th>
                  <th scope="col">GitHub issues</th>
                  <th scope="col">Size / focused work</th>
                  <th scope="col">Owner / Codex task</th>
                  <th scope="col">Next proof</th>
                </tr>
              </thead>
              ${outcomeGroups.map(outcomeGroupMarkup).join("")}
            </table>
          </div>
        </section>

        <section class="asr-not-blockers" aria-labelledby="not-blockers-title">
          <div>
            <p class="eyebrow">Explicit v1 scope</p>
            <h2 id="not-blockers-title">Urgent or useful does not automatically mean submission-blocking.</h2>
          </div>
          <ul>
            <li><strong>Social login, Sign in with Apple, and passkeys</strong><span>Not a v1 gate while first-party email is the only login service.</span></li>
            <li><strong>Android parity</strong><span>Not required for the first iPhone submission.</span></li>
            <li><strong>Xcode Cloud</strong><span>Useful later; the reviewed manual signed lane remains the release path.</span></li>
            <li><strong>Browser-extension parity</strong><span>Not required for the first iPhone submission.</span></li>
            <li><strong>Rich-source media</strong><span>Active product work remains separate from the first submission; it is not a submission gate unless launch copy promises it.</span></li>
            <li>
              <strong>Semantic search</strong>
              <span>Active product work remains separate from the first submission; it is not a submission gate unless launch copy promises it.</span>
              ${taskMarkup(tasks.search)}
            </li>
          </ul>
        </section>

        <footer class="asr-footer">
          <p>
            Frozen 5 Aug 2026 · 01:05 EDT from GitHub and release readback through
            <a href="${GITHUB}/commit/e5802423f3bb410b5d74ad65ec1b7bd6ca14656d" target="_blank" rel="noreferrer">main@e580242</a>.
            GitHub remains the durable project source of truth. Apple/device states remain distinct from source or simulator evidence.
          </p>
          <div>
            <a href="${GITHUB}/milestone/17" target="_blank" rel="noreferrer">Milestone #17 ↗</a>
            <a href="${APPLE_GUIDELINES}" target="_blank" rel="noreferrer">App Review Guidelines ↗</a>
          </div>
        </footer>
      </main>
    </div>`;

  requestAnimationFrame(() => {
    document.querySelector<HTMLElement>("#app-store-main")?.focus();
  });
}
